import { i18n } from '@lingui/core';
import dpde from 'date-fns/locale/de';
import dpenAU from 'date-fns/locale/en-AU';
import dpenCA from 'date-fns/locale/en-CA';
import dpenGB from 'date-fns/locale/en-GB';
import dpenIE from 'date-fns/locale/en-IE';
import dpenIN from 'date-fns/locale/en-IN';
import dpenNZ from 'date-fns/locale/en-NZ';
import dpenUS from 'date-fns/locale/en-US';
import dpenZA from 'date-fns/locale/en-ZA';
import dpes from 'date-fns/locale/es';
import dpfr from 'date-fns/locale/fr';
import dpit from 'date-fns/locale/it';
import dpnl from 'date-fns/locale/nl';
import dppt from 'date-fns/locale/pt';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

import { LANG_FALLBACK, LANG_SUPPORTED, LOCAL_STORAGE_APPLANG, LOCAL_STORAGE_FORCEDLANG } from '../constants';
import { messages as deMessages } from '../locales/de/messages';
import { messages as enMessages } from '../locales/en/messages';
import { messages as esMessages } from '../locales/es/messages';
import { messages as frMessages } from '../locales/fr/messages';
import { messages as itMessages } from '../locales/it/messages';
import { messages as nlMessages } from '../locales/nl/messages';
import { messages as ptMessages } from '../locales/pt/messages';
import { devMessage } from '../settings/settings';

const DEFAULT_REGIONAL_LOCALES = { en: 'en-GB' };

const DATE_PICKER_ENGLISH_LOCALES = {
  'en-AU': dpenAU,
  'en-CA': dpenCA,
  'en-GB': dpenGB,
  'en-IE': dpenIE,
  'en-IN': dpenIN,
  'en-NZ': dpenNZ,
  'en-US': dpenUS,
  'en-ZA': dpenZA,
};

let dateLocale = DEFAULT_REGIONAL_LOCALES[LANG_FALLBACK];

const getBrowserPreferredLocales = () => (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]);

// This returns an array of 2 values (if possible else 1) => [mainLocale, secondaryLocale] eg: ["fr","ca"]
// Our locale/lang lib (linguiJS) doesnt understand 'FR' or 'fr-CA' so it will use the mainLocale which would be 'fr' in our example
// Our phone field or other localized fields will use the secondaryLocale to have a canadian phone prefix in our example
export const getBrowserLocales = () => {
  const userLocale = getBrowserPreferredLocales()[0];
  const formattedLocale = userLocale.toLowerCase();
  if (formattedLocale.includes('-')) {
    return formattedLocale.split('-');
  }
  return [formattedLocale];
};

const getSupportedTranslationLang = browserMainLocale => (LANG_SUPPORTED.includes(browserMainLocale) ? browserMainLocale : LANG_FALLBACK);

const LOCALE_REGION_REGEX = /^([a-z]{2}|\d{3})$/;

const parseLocale = locale => {
  const [language, ...subtags] = String(locale ?? '')
    .toLowerCase()
    .split(/[-_]/);
  const extensionIndex = subtags.findIndex(subtag => subtag.length === 1);
  const baseSubtags = extensionIndex === -1 ? subtags : subtags.slice(0, extensionIndex);
  const region = baseSubtags.find(subtag => LOCALE_REGION_REGEX.test(subtag));
  return { language, region: region?.toUpperCase() };
};

// The dates are formatted with the browser region only if the browser language is the translation lang
// eg: browser "en-US" + lang "en" => "en-US" but browser "fr-FR" + lang "en" => "en-GB"
export const getRegionalLocale = (lang, preferredLocales) => {
  for (const preferredLocale of preferredLocales) {
    const { language, region } = parseLocale(preferredLocale);
    if (language !== lang) break;
    if (region) return `${language}-${region}`;
  }
  return DEFAULT_REGIONAL_LOCALES[lang] ?? lang;
};

export const getDateLocale = () => dateLocale;

export const getDatePickerLocale = () => {
  if (DATE_PICKER_ENGLISH_LOCALES[dateLocale]) return dateLocale;
  return i18n.locale === 'en' ? DEFAULT_REGIONAL_LOCALES.en : i18n.locale;
};

// THIS is responsible for the interface LANG !
// HOW DOES IT WORK:
// ---- IF user has selected lang (from the footer dropdown) ==> OK (The user selected locale is common to all BEs!)
// ---- ELSE IF the BE has a forced lang (from the BE config) => OK
// ---- ELSE we use the browser locale
// FINALLY on that lang, we use it if we support it, IF NOT then we fallback to LANG_FALLBACK
export const getLangForTranslations = doLog => {
  const userSelectedLang = getUserSelectedLang();
  const beForcedLang = localStorage.getItem(LOCAL_STORAGE_FORCEDLANG);
  const automaticLang = getBrowserLocales()[0];

  if (doLog) {
    devMessage('userLang=', userSelectedLang, 'forcedLang=', beForcedLang, 'autoLang=', automaticLang);
  }

  if (userSelectedLang != null) return getSupportedTranslationLang(userSelectedLang);
  if (beForcedLang != null && beForcedLang != '') return getSupportedTranslationLang(beForcedLang);
  return getSupportedTranslationLang(automaticLang);
};

export const initTranslations = bookingEngineForcedLang => {
  // we set/replace the forced lang (even if null)
  localStorage.setItem(LOCAL_STORAGE_FORCEDLANG, bookingEngineForcedLang ?? '');

  const locale = getLangForTranslations(true);
  devMessage('Lang: will actually use supported lang:', locale);

  // Load and activate the lingui translations
  i18n.load({
    de: deMessages,
    en: enMessages,
    es: esMessages,
    fr: frMessages,
    it: itMessages,
    nl: nlMessages,
    pt: ptMessages,
  });
  i18n.activate(locale);

  dateLocale = getRegionalLocale(locale, getBrowserPreferredLocales());
  devMessage('Lang: dates will be formatted with:', dateLocale);

  // Same for the datepicker locale management
  registerLocale('de', dpde);
  registerLocale('es', dpes);
  registerLocale('fr', dpfr);
  registerLocale('it', dpit);
  registerLocale('nl', dpnl);
  registerLocale('pt', dppt);
  for (const [englishLocale, datePickerLocale] of Object.entries(DATE_PICKER_ENGLISH_LOCALES)) {
    registerLocale(englishLocale, datePickerLocale);
  }
  setDefaultLocale(getDatePickerLocale());
};

export const getUserSelectedLang = () => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_APPLANG);
  } catch (error) {
    return null;
  }
};
